/**
 * Desktop-only: capture a chosen system output (render endpoint) and expose its
 * audio as a `MediaStream`.
 *
 * Bridges the Tauri `start_remote_tap` command's i16 PCM frames (delivered over a
 * Tauri Channel) into an AudioWorklet sink (`createRemotePcmSink`). The returned
 * stream is recorded to webm/opus by the remote-channel hook and streamed to the
 * backend as `?channel=remote`.
 *
 * Which output to capture is decided by the caller (`useRemoteChannelStreaming`'s
 * lock-on-first-audio logic), so `startTap`/`retargetTap` always take an explicit
 * target. `retargetTap` swaps the native source while keeping the SAME sink alive
 * (so the MediaRecorder + WebSocket never close) and binds a FRESH Channel each
 * time — reusing a Channel across a stop/start orphans its callback and silently
 * drops frames.
 *
 * No-ops outside Tauri; all `@tauri-apps/api` imports are dynamic so the web
 * bundle never pulls them in.
 */
import { useCallback, useRef } from "react"
import { createRemotePcmSink, type IRemotePcmSink } from "../lib/remote-capture"
import { IS_TAURI } from "../lib/runtime-origin"

export interface ITapStart {
  /** The remote audio stream a MediaRecorder records to webm/opus. */
  stream: MediaStream
  /** The output id being captured. */
  targetId: string
}

interface IUseRemoteTapStreamResult {
  /** Start capturing `targetId` into a fresh sink. Null off-desktop / on failure. */
  startTap: (targetId: string) => Promise<ITapStart | null>
  /**
   * Re-point the native source at a different output WITHOUT touching the JS audio
   * pipeline — the sink, MediaRecorder and WebSocket stay alive, so the backend
   * never sees a channel close (no finalize, no transcript gap). A fresh Channel
   * is bound each time. Returns the new target id, or undefined if no tap is
   * running / the swap failed.
   */
  retargetTap: (targetId: string) => Promise<string | undefined>
  /** Stop the native tap and tear down the sink. */
  stopTap: () => Promise<void>
}

/** Build a Channel onmessage handler that decodes LE i16 frames into the sink. */
function makeFrameHandler(sink: IRemotePcmSink): (msg: ArrayBuffer | number[]) => void {
  let frames = 0
  return (msg): void => {
    const bytes = msg instanceof ArrayBuffer ? new Uint8Array(msg) : Uint8Array.from(msg)
    const count = Math.floor(bytes.byteLength / 2)
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const i16 = new Int16Array(count)
    let peak = 0
    for (let i = 0; i < count; i++) {
      const s = view.getInt16(i * 2, true)
      i16[i] = s
      const a = s < 0 ? -s : s
      if (a > peak) peak = a
    }
    sink.push(i16)
    frames++
    if (frames === 1 || frames % 20 === 0) {
      console.info(`[remote-capture] tap frames=${frames} peak=${peak}/32767`)
    }
  }
}

export function useRemoteTapStream(): IUseRemoteTapStreamResult {
  const sinkRef = useRef<IRemotePcmSink | null>(null)

  const startTap = useCallback(async (targetId: string): Promise<ITapStart | null> => {
    if (!IS_TAURI) {
      console.info("[remote-capture] not running in Tauri — skipping system-audio tap")
      return null
    }
    const { Channel, invoke } = await import("@tauri-apps/api/core")
    const sink = await createRemotePcmSink(16_000)
    sinkRef.current = sink

    const channel = new Channel<ArrayBuffer | number[]>()
    channel.onmessage = makeFrameHandler(sink)

    try {
      await invoke("start_remote_tap", { onFrames: channel, targetId })
      console.info(`[remote-capture] capturing output ${targetId} — waiting for frames…`)
    } catch (err) {
      console.error("[remote-capture] start_remote_tap FAILED:", err)
      await sink.close()
      sinkRef.current = null
      throw err
    }
    return { stream: sink.stream, targetId }
  }, [])

  const retargetTap = useCallback(async (targetId: string): Promise<string | undefined> => {
    const sink = sinkRef.current
    if (!sink) return undefined
    const { Channel, invoke } = await import("@tauri-apps/api/core")
    try {
      await invoke("stop_remote_tap")
    } catch {
      // may already be stopped
    }
    // Fresh Channel bound to the SAME sink — recorder + WS keep running.
    const channel = new Channel<ArrayBuffer | number[]>()
    channel.onmessage = makeFrameHandler(sink)
    try {
      await invoke("start_remote_tap", { onFrames: channel, targetId })
      console.info(`[remote-capture] retargeted → ${targetId} (fresh channel, pipeline kept alive)`)
      return targetId
    } catch (err) {
      console.error("[remote-capture] retarget start_remote_tap failed:", err)
      return undefined
    }
  }, [])

  const stopTap = useCallback(async (): Promise<void> => {
    if (!sinkRef.current) return
    try {
      const { invoke } = await import("@tauri-apps/api/core")
      await invoke("stop_remote_tap")
    } catch {
      // best-effort
    }
    await sinkRef.current.close()
    sinkRef.current = null
  }, [])

  return { startTap, retargetTap, stopTap }
}
